import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { listTenants, createTenant, Tenant } from '../api/client';
import { useTranslation } from 'react-i18next';

/**
 * Workspace setup page shown when a user has no active tenant.
 * Allows creating a new workspace or selecting an existing one.
 */
export default function WorkspaceSetup() {
  const { t } = useTranslation();
  const { user, switchTenant } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await listTenants();
        setTenants(result.tenants);
      } catch (err) {
        console.error('Failed to load tenants:', err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setIsCreating(true);
    setError(null);
    try {
      const tenant = await createTenant(newName.trim());
      await switchTenant(tenant.id);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create workspace');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelect = async (tenantId: string) => {
    try {
      await switchTenant(tenantId);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to select workspace');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="workspace-gate" className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {t('workspace.setup_title', 'Set Up Your Workspace')}
        </h1>
        <p className="text-gray-600 mb-6">
          {t('workspace.setup_description', 'Create a new workspace or join an existing one to get started.')}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Existing tenants */}
        {tenants.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-medium text-gray-700 mb-2">
              {t('workspace.your_workspaces', 'Your Workspaces')}
            </h2>
            <div className="space-y-2">
              {tenants.map((tenant) => (
                <button
                  key={tenant.id}
                  data-testid="workspace-select"
                  onClick={() => handleSelect(tenant.id)}
                  className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  <span className="font-medium text-gray-900">{tenant.name}</span>
                  <span className="text-xs text-gray-500 capitalize">{tenant.role}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Create new workspace */}
        <div className="border-t pt-6">
          <h2 className="text-sm font-medium text-gray-700 mb-2">
            {t('workspace.create_new', 'Create New Workspace')}
          </h2>
          <form onSubmit={handleCreate} className="flex gap-2">
            <input
              type="text"
              data-testid="workspace-name-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t('workspace.name_placeholder', 'Workspace name')}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isCreating}
            />
            <button
              type="submit"
              data-testid="workspace-create-submit"
              disabled={isCreating || !newName.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isCreating ? '...' : t('workspace.create_button', 'Create')}
            </button>
          </form>
        </div>

        <p className="mt-6 text-xs text-gray-500 text-center">
          {t('workspace.signed_in_as', 'Signed in as')} {user?.email}
        </p>
      </div>
    </div>
  );
}
